import moment from 'moment';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { requestBooking } from 'state/modules/booking';
import { clearStore, loadActiveContracts } from 'state/modules/contract';
import get from '@htmniseko/htm-helpers/misc/get';
import set from '@htmniseko/htm-helpers/misc/set';
import { NoDataFound } from 'components';
import { RequestBookingForm } from 'components/Form';
import { useBedConfig } from 'context/BedConfigContext';
import usePush from 'hooks/router/usePush';
import { isExcludedOwnerRoom, isVisibleOwnerProperty } from 'utils/ownerRooms';
import { getMessage } from 'utils/errors';

const NC_BLOCK_ID = 1;
const REQUIRED_FIELDS = [
  'room',
  'stay.from',
  'stay.to',
  'pax.adults',
  'pax.children',
  'pax.infants',
  'paymentMethod',
];

const defaultValues = {
  pax: {
    adults: 1,
    children: 0,
    infants: 0,
  },
  bedConfiguration: 'I\'ll decide later',
  cleaning: 'Default',
  ownerStorageKey: 'No',
  paymentMethod: 'Owner Account',
};

export class RequestBooking extends PureComponent {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    bedConfigText: PropTypes.object.isRequired,
    contractStore: PropTypes.object.isRequired,
    tenantId: PropTypes.string.isRequired,
    clearStore: PropTypes.func.isRequired,
    loadActiveContracts: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    requestBooking: PropTypes.func.isRequired,
    intl: PropTypes.object,
  };

  state = {
    booking: { ...defaultValues, bedConfiguration: this.decideLater },
    bedConfigs: [],
    beddingRooms: [],
    roomExcluded: false,
    errors: [],
    allowCleaningOption: false,
    isSubmitting: false,
  };

  // Load owner's contracts after component mounted
  componentDidMount() {
    const { auth: { user } } = this.props;
    // Include Q-Rooms
    const includes = { qRooms: true };
    this.props.loadActiveContracts(user.id, includes);
  }

  componentWillUnmount() {
    // Clean up redux store when unmounting the tab
    this.props.clearStore();
  }

  componentDidUpdate(prevProps) {
    if (this.state.booking.room && !this.props.contractStore.loading && (
      prevProps.contractStore.contracts !== this.props.contractStore.contracts ||
      prevProps.bedConfigText !== this.props.bedConfigText
    )) {
      const { booking, beddingRooms } = this.state;
      this.setState(this.getNewBookingDetails(booking, booking.room, beddingRooms));
    }
  }

  get decideLater() {
    return this.props.intl?.formatMessage({ id: 'form.requestBooking.decideLater', defaultMessage: "I'll decide later" }) || "I'll decide later";
  }

  getBeddingSummary(rooms) {
    return rooms.map(room => `${room.code}: ${room.value}`).join('; ');
  }

  handleBack() {
    this.setState({ isSubmitting: false });
  }

  handleChange(key, value) {
    // Handling room selection
    if (key === 'room') {
      this.handleRoomSelect(value);
    }
    else if (key.startsWith('bedding.')) {
      const id = Number(key.substring('bedding.'.length));
      this.setState(prevState => {
        const beddingRooms = prevState.beddingRooms.map(room => room.pmsRoomId === id ? { ...room, value } : room);
        return { beddingRooms, booking: { ...prevState.booking, bedConfiguration: this.getBeddingSummary(beddingRooms) } };
      });
    }
    else {
      // Update booking values in state
      this.setState(prevState => {
        const booking = { ...prevState.booking };
        // Update new changes
        set(booking, key, value);
        return { booking };
      });
    }
  }

  handleRoomSelect(roomCode) {
    this.setState(prevState => ({ ...this.getNewBookingDetails(prevState.booking, roomCode), roomExcluded: false }));
  }

  async handleSubmit() {
    const { auth: { admin, user, token }, tenantId, push } = this.props;
    // Include requester info into the data before requesting
    const booking = { ...this.state.booking, requester: admin ? 'admin' : 'owner' };
    try {
      // requestBooking returns the action promise from clientMiddleware; await so
      // we only navigate on success. Failures surface via the booking module's
      // LOAD_FAIL → notification pipeline.
      await this.props.requestBooking(user.id, booking, tenantId, token);
      this.setState({
        booking: { ...defaultValues, bedConfiguration: this.decideLater },
        errors: [],
        isSubmitting: false,
        beddingRooms: [],
        bedConfigs: [],
        roomExcluded: false,
      });
      // Redirect to the existing-bookings list so the user has visible feedback
      // (the success toast comes from the booking action's notifications config).
      push('/booking/existing');
    }
    catch (error) {
      if (getMessage(error)?.code === 'OWNER_ROOM_EXCLUDED') {
        this.setState(prevState => ({
          ...this.getNewBookingDetails(prevState.booking, ''),
          isSubmitting: false,
          roomExcluded: true,
        }));
        this.props.loadActiveContracts(user.id, { qRooms: true });
        return;
      }
      // Stay on the confirmation view so the user can retry. The notification
      // middleware will surface the error toast.
      this.setState({ isSubmitting: false });
    }
  }

  handleRequest() {
    const { booking } = this.state;

    if (isExcludedOwnerRoom(booking.room)) {
      this.setState(prevState => ({ ...this.getNewBookingDetails(prevState.booking, ''), roomExcluded: true }));
      return;
    }

    // Check if any required field is empty or undefined
    const errors = REQUIRED_FIELDS.filter(key => (get(booking, key) === undefined || get(booking, key) === ''));

    // Check if startDate and endDate are valid
    const { from, to } = booking?.stay ?? {};

    if (from && to && moment(to).isSameOrBefore(moment(from))) {
      errors.push('Invalid date range');
    }

    // Submit if there is no errors
    if (errors.length === 0) {
      this.setState({ booking, isSubmitting: true });
    }

    // Update the errors array in state
    this.setState({ errors });
  }

  getNewBookingDetails(booking, roomCode, previousRooms) {
    const { bedConfigText, contractStore: { contracts } } = this.props;
    const newBooking = { ...booking };

    // Get the selected room contract
    const contract = contracts.find(contract => contract?.property?.code === roomCode);

    // Update new changes
    set(newBooking, 'room', roomCode);
    // Add tenant ID
    set(newBooking, 'tenantId', contract?.property?.blockId);

    // Get the bed configuration IDs from the contract
    const bedConfigs = contract?.property?.bedConfigurations ?? [];
    const bedConfigOptions = bedConfigs.map(id => bedConfigText[id]).filter(Boolean);
    const beddingRooms = (contract?.property?.beddingRooms ?? []).map(room => {
      const options = (room.bedConfigurations ?? []).map(id => bedConfigText[id]).filter(Boolean);
      const previous = previousRooms?.find(oldRoom => oldRoom.pmsRoomId === room.pmsRoomId);
      return {
        pmsRoomId: room.pmsRoomId,
        code: room.code,
        bedConfigs: options,
        value: previous && options.includes(previous.value) ? previous.value : this.decideLater,
      };
    });
    newBooking.bedConfiguration = beddingRooms.length > 0 ?
      this.getBeddingSummary(beddingRooms)
      : previousRooms && bedConfigOptions.includes(booking.bedConfiguration) ? booking.bedConfiguration : this.decideLater;

    // Determine if the room should have cleaning options
    // Return true for Niseko Central room
    const allowCleaningOption = contract?.property?.blockId === NC_BLOCK_ID;

    return {
      allowCleaningOption,
      booking: newBooking,
      bedConfigs: bedConfigOptions,
      beddingRooms,
    };
  }

  render() {
    const { allowCleaningOption, booking, bedConfigs, beddingRooms, roomExcluded, errors, isSubmitting } = this.state;
    const { tenantId } = this.props;
    const { loading, contracts } = this.props.contractStore;
    const rooms = contracts.filter(c => isVisibleOwnerProperty(c.property)).map(c => c?.property?.code).filter(Boolean);
    // Find the selected room
    const room = contracts.find(c => c?.property?.code === booking.room);
    // Return true for Niseko Central room
    const shouldRenderCleaning = room?.property?.blockId === NC_BLOCK_ID;

    // Display confirmation before the final submission
    if (isSubmitting) {
      return (
        <RequestBookingForm
          tenantId={tenantId}
          allowCleaningOption={shouldRenderCleaning}
          data={booking}
          onBack={() => this.handleBack()}
          onSubmit={() => this.handleSubmit()}
        />
      );
    }

    if (loading) {
      return null;
    }

    if (rooms.length < 1) {
      return <NoDataFound />;
    }

    // Request booking form
    return (
      <RequestBookingForm
        tenantId={tenantId}
        allowCleaningOption={allowCleaningOption}
        bedConfigs={bedConfigs}
        beddingRooms={beddingRooms}
        roomExcluded={roomExcluded}
        data={booking}
        errors={errors}
        roomNames={rooms}
        onChange={(key, value) => this.handleChange(key, value)}
        onSubmit={() => this.handleRequest()}
      />
    );
  }
}

const ConnectedRequestBooking = connect(
  state => ({
    auth: state.auth,
    tenantId: state.tenant.id,
    contractStore: state.contract,
  }),
  { clearStore, loadActiveContracts, requestBooking }
)(RequestBooking);

const RequestBookingWithDeps = props => {
  const bedConfigText = useBedConfig();
  const intl = useIntl();
  const { push } = usePush();
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <ConnectedRequestBooking {...props} bedConfigText={bedConfigText} intl={intl} push={push} />;
};

export default RequestBookingWithDeps;
